import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectFilteredEdgeNodes } from '../map/slice/mapSelectors';
import { generateColor } from '../utils/colors';
import {
  selectPaths,
  pathSelected,
  zoomToPath,
  Path,
} from '../paths/pathsSlice';
import { MapTooltip } from './MapTooltip';
import { priceFormatter } from '../helpers/formatters';
import { edgeToGeoJson } from '../utils/geoJsonUtils';
import { RootState } from '@/store';
import { useMapLayer } from '../hooks/useMapLayer';
import { useMapContext, MapEventListener } from './context/MapContext';
// import * as pathsActions from './pathsSlice';

const getEdgeStyle = () => [
  {
    filter: [
      'all',
      ['==', ['get', 'isSelected'], 0],
      ['<=', ['get', 'head'], 0],
    ],
    style: {
      'stroke-width': ['get', 'width'],
      'stroke-color': ['get', 'color'],
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
      // 'stroke-offset': -15,
      'shape-angle': 0,
    },
  },
  {
    filter: [
      'all',
      ['==', ['get', 'isSelected'], 0],
      ['==', ['get', 'head'], 1],
    ],
    style: {
      'shape-points': 3,
      'shape-radius': ['get', 'arrowheadWidth'],
      'shape-fill-color': ['get', 'arrowHeadColor'],
      'shape-stroke-color': ['get', 'arrowHeadColor'],
      'shape-stroke-width': 4,
      'shape-rotate-with-view': false,
      // 'shape-displacement': [15, 15],
      'shape-opacity': 1,
      'shape-angle': ['get', 'angle'],
    },
  },
  {
    filter: [
      'all',
      ['==', ['get', 'isSelected'], 1],
      ['<=', ['get', 'head'], 0],
    ],
    style: {
      'stroke-width': ['get', 'width'],
      'stroke-color': ['get', 'color'],
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
      // 'stroke-offset': -15,
      'shape-angle': 0,
    },
  },
  {
    filter: [
      'all',
      ['==', ['get', 'isSelected'], 1],
      ['==', ['get', 'head'], 1],
    ],
    style: {
      'shape-points': 3,
      'shape-radius': ['get', 'arrowheadWidth'],
      'shape-fill-color': ['get', 'arrowHeadColor'],
      'shape-stroke-color': ['get', 'arrowHeadColor'],
      'shape-stroke-width': 4,
      'shape-rotate-with-view': false,
      // 'shape-displacement': [15, 15],
      'shape-opacity': 1,
      'shape-angle': ['get', 'angle'],
    },
  },
];

export type MapChainsProps = {
  zIndex: number;
};

export const MapChains: React.FC<MapChainsProps> = ({ zIndex }) => {
  const {
    map,
    registerHoverListener,
    unregisterHoverListener,
    registerClickListener,
    unregisterClickListener,
  } = useMapContext();

  const dispatch = useDispatch();
  const selectedPathId = useSelector(
    (state: RootState) => state.paths.selectedPathId,
  );
  const visiblePathIds = useSelector(
    (state: RootState) => state.paths.visiblePathIds,
  );
  const edgeNodes = useSelector(selectFilteredEdgeNodes);
  const paths = useSelector(selectPaths);

  const [popupPath, setPopupPath] = useState<Path | null>(null);

  const hoverListener: MapEventListener = useCallback(
    (feature) => {
      if (feature?.getProperties()?.key === `path-${popupPath?.id}`) return;
      if (!feature?.getProperties()?.key?.startsWith('path-')) {
        setPopupPath(null);
        return;
      }
      const pathId = feature?.getProperties()?.id;
      const path = paths?.[pathId];
      setPopupPath(path);
    },
    [paths],
  );

  const clickListener: MapEventListener = useCallback(
    (feature) => {
      if (!feature || !feature?.getProperties()?.key?.startsWith('path-'))
        return;
      if (feature?.getProperties()?.key === `path-${selectedPathId}`) return;

      const pathId = feature.getProperties()?.id;
      dispatch(
        pathSelected({
          selectedPathId: pathId,
        }),
      );
      dispatch(zoomToPath(pathId));
    },
    [selectedPathId, pathSelected, zoomToPath, dispatch],
  );

  const chainFeatures = useMemo(
    () =>
      Object.keys(paths)
        .filter(
          (pathId) =>
            paths[pathId] &&
            paths[pathId].operation_chain &&
            (visiblePathIds.includes(pathId) ||
              pathId?.toString() === selectedPathId?.toString()),
        )
        .map((pathId) => paths[pathId])
        .sort((pathA, pathB) => {
          if (Number(pathA.id) === selectedPathId) return 1;
          if (Number(pathB.id) === selectedPathId) return -1;
          return pathB.operation_chain?.profit - pathA.operation_chain?.profit;
        })
        .flatMap((path) => {
          const pathId = Number(path.id);
          const isSelected = pathId === selectedPathId;
          const pathNodeIds = path.path_node_ids;
          const loadedPathNodeIds = pathNodeIds.filter((pathNodeId) =>
            Object.keys(edgeNodes).includes(String(pathNodeId)),
          );
          const pathPositions = loadedPathNodeIds
            .filter((nodeId) => Boolean(edgeNodes[nodeId]))
            .map(
              (nodeId) =>
                [
                  Number(edgeNodes[nodeId].lat),
                  Number(edgeNodes[nodeId].lng),
                ] as [number, number],
            );

          if (loadedPathNodeIds.length < 2) return [];
          return edgeToGeoJson(
            { positions: pathPositions, isSelected, pathId },
            () => {
              const color = generateColor(pathId, 70, 50);
              const arrowHeadColor = generateColor(pathId, 70, 60);
              return {
                id: pathId,
                key: `path-${pathId}`,
                width: isSelected ? 14 : 10,
                arrowheadWidth: isSelected ? 20 : 16,
                isSelected: isSelected ? 1 : 0,
                color,
                arrowHeadColor,
              };
            },
            true,
            { frequency: isSelected ? 0.8 : 0.5 },
          );
        }),
    [selectedPathId, paths, visiblePathIds, edgeNodes],
  );

  const style = useMemo(() => getEdgeStyle(), []);

  useMapLayer(chainFeatures, { zIndex, style });

  useEffect(() => {
    if (!map) return;
    registerClickListener(clickListener);
    registerHoverListener(hoverListener);
    return () => {
      unregisterClickListener(clickListener);
      unregisterHoverListener(hoverListener);
    };
  }, [map, clickListener, hoverListener]);

  return (
    <MapTooltip visible={Boolean(popupPath)}>
      {popupPath && (
        <div className="bg-dark p-2 px-3 border-dark text-light rounded">
          {Boolean(popupPath) && (
            <div className="d-flex flex-column">
              <div className="fs-6 fw-bold">Chain #{popupPath.id}</div>
              <div>
                $/dth:{' '}
                {priceFormatter.format(
                  Number(
                    Number(
                      popupPath.operation_chain.profit /
                        popupPath.operation_chain.delivered_dth,
                    ).toFixed(3),
                  ),
                )}
              </div>
              <div>
                Volume:{' '}
                {Number(popupPath.operation_chain.delivered_dth).toFixed(0)}
                dth
              </div>
              <div>
                Profit:{' '}
                {priceFormatter.format(
                  Number(Number(popupPath.operation_chain.profit).toFixed(2)),
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </MapTooltip>
  );
};
